import React, { useCallback, useEffect, useState } from 'react';
import axios from '../config/api';
import { formatCurrency } from '../utils/format';
import { sortNewestFirst } from '../utils/newestFirst';
import {
  getIssueTypeLabel,
  getResolutionLabel,
  ISSUE_STATUS_LABELS,
  REFUND_OUTCOME_LABELS,
  getRefundOutcomeLabel,
} from '../content/orderIssueTypes';

function IssuePhotoThumb({ url }: { url: string }) {
  const [src, setSrc] = useState('');

  useEffect(() => {
    let objectUrl = '';
    let cancelled = false;
    const needsAuth = url.includes('/api/orders/issue-photos/');
    if (!needsAuth) {
      setSrc(url);
      return undefined;
    }
    axios
      .get(url, { responseType: 'blob' })
      .then((response) => {
        objectUrl = URL.createObjectURL(response.data);
        if (!cancelled) setSrc(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (!src) {
    return <div className="h-20 w-20 rounded-lg border bg-gray-100" />;
  }

  return (
    <button
      type="button"
      onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
      className="block h-20 w-20 overflow-hidden rounded-lg border"
    >
      <img src={src} alt="Issue report photo" className="h-full w-full object-cover" />
    </button>
  );
}

export interface StaffOrderIssue {
  id: string;
  order_id: string;
  order_number?: string;
  order_status?: string;
  total_amount?: number;
  user_name: string;
  user_email: string;
  cafe_id: string;
  cafe_name: string;
  issue_type: string;
  affected_items: unknown[];
  description: string;
  photo_urls: string[];
  resolution_preference: string;
  status: string;
  refund_outcome?: string;
  estimated_refund_amount?: number;
  refund_breakdown?: Array<{
    menu_item_id: string;
    name: string;
    quantity: number;
    unit_price: number;
    line_total: number;
  }>;
  created_at: string;
  updated_at: string;
}

interface OrderIssuesPanelProps {
  selectedCafe: string;
}

const STATUS_OPTIONS = Object.keys(ISSUE_STATUS_LABELS);
const REFUND_OPTIONS = Object.keys(REFUND_OUTCOME_LABELS);

function formatRefundOptionLabel(refundOutcome: string, amount?: number): string {
  const baseLabel = getRefundOutcomeLabel(refundOutcome);
  const showAmount = (refundOutcome === 'full' || refundOutcome === 'partial') && (amount ?? 0) > 0;
  return showAmount ? `${baseLabel} (${formatCurrency(amount || 0)})` : baseLabel;
}

export default function OrderIssuesPanel({ selectedCafe }: OrderIssuesPanelProps) {
  const [issues, setIssues] = useState<StaffOrderIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<Record<string, string>>({});
  const [draftRefund, setDraftRefund] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const loadIssues = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {};
      if (selectedCafe) params.cafe_id = selectedCafe;
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get<{ issues?: StaffOrderIssue[] }>('/api/barista/order-issues', { params });
      setIssues(sortNewestFirst(res.data.issues || [], (issue) => issue.created_at));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Could not load issue reports';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [selectedCafe, statusFilter]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const handleSaveStatus = async (issue: StaffOrderIssue) => {
    const nextStatus = draftStatus[issue.id] ?? issue.status;
    const nextRefund = draftRefund[issue.id] ?? issue.refund_outcome ?? 'pending';
    if (nextStatus === issue.status && nextRefund === (issue.refund_outcome || 'pending')) return;
    setSavingId(issue.id);
    try {
      const res = await axios.patch(`/api/barista/order-issues/${issue.id}/status`, {
        status: nextStatus,
        refund_outcome: nextRefund,
      });
      setIssues((prev) => prev.map((i) => (i.id === issue.id ? res.data : i)));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to update status';
      alert(msg);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem]">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Filter by status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {ISSUE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={loadIssues}
          className="rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800"
        >
          Refresh
        </button>
      </div>

      {loading && <p className="text-gray-600">Loading issue reports…</p>}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && issues.length === 0 && (
        <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-gray-600">
          No issue reports yet{selectedCafe ? ' for this café' : ''}.
        </p>
      )}
      {!loading && !error && issues.length > 0 && (
        <p className="text-xs text-gray-500">Showing newest reports first.</p>
      )}

      <div className="space-y-3">
        {issues.map((issue) => {
          const expanded = expandedId === issue.id;
          const currentDraft = draftStatus[issue.id] ?? issue.status;
          const currentRefund = draftRefund[issue.id] ?? issue.refund_outcome ?? 'pending';
          const savedRefund = issue.refund_outcome || 'pending';
          const estimatedAmount = issue.estimated_refund_amount || 0;
          const hasChanges =
            currentDraft !== issue.status || currentRefund !== savedRefund;
          return (
            <article
              key={issue.id}
              className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
            >
              <button
                type="button"
                className="flex w-full flex-wrap items-start justify-between gap-2 px-4 py-3 text-left hover:bg-gray-50"
                onClick={() => setExpandedId(expanded ? null : issue.id)}
              >
                <div>
                  <p className="font-semibold text-gray-900">
                    {issue.order_number || issue.order_id.slice(0, 8)} · {issue.cafe_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {getIssueTypeLabel(issue.issue_type)} · {issue.user_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(issue.created_at).toLocaleString()}
                  </p>
                </div>
                <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
                  {ISSUE_STATUS_LABELS[issue.status] || issue.status}
                  {savedRefund !== 'pending' && (
                    <span className="block text-[10px] font-normal mt-0.5">
                      {formatRefundOptionLabel(savedRefund, estimatedAmount)}
                    </span>
                  )}
                </span>
              </button>

              {expanded && (
                <div className="border-t border-gray-100 px-4 py-4 space-y-3 text-sm">
                  <p>
                    <span className="font-semibold">Customer:</span> {issue.user_name} (
                    {issue.user_email})
                  </p>
                  {issue.total_amount != null && (
                    <p>
                      <span className="font-semibold">Order total:</span>{' '}
                      {formatCurrency(issue.total_amount)}
                    </p>
                  )}
                  <p>
                    <span className="font-semibold">Requested resolution:</span>{' '}
                    {getResolutionLabel(issue.resolution_preference)}
                  </p>
                  <p className="whitespace-pre-wrap">{issue.description}</p>

                  {(issue.refund_breakdown?.length ?? 0) > 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                        Estimated refund (reported)
                      </p>
                      <ul className="space-y-1">
                        {issue.refund_breakdown!.map((row) => (
                          <li key={row.menu_item_id} className="flex justify-between gap-2">
                            <span>
                              {row.quantity}× {row.name}
                            </span>
                            <span className="font-medium">{formatCurrency(row.line_total)}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="flex justify-between border-t border-amber-200 mt-2 pt-2 font-bold">
                        <span>Total</span>
                        <span>{formatCurrency(issue.estimated_refund_amount ?? 0)}</span>
                      </p>
                    </div>
                  )}

                  {issue.photo_urls?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {issue.photo_urls.map((url) => (
                        <IssuePhotoThumb key={url} url={url} />
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-gray-100">
                    <div className="min-w-[10rem] flex-1">
                      <label className="mb-1 block text-xs font-semibold text-gray-500">
                        Case status
                      </label>
                      <select
                        value={currentDraft}
                        onChange={(e) =>
                          setDraftStatus((prev) => ({ ...prev, [issue.id]: e.target.value }))
                        }
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {ISSUE_STATUS_LABELS[s]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="min-w-[10rem] flex-1">
                      <label className="mb-1 block text-xs font-semibold text-gray-500">
                        Refund decision
                      </label>
                      <select
                        value={currentRefund}
                        onChange={(e) =>
                          setDraftRefund((prev) => ({ ...prev, [issue.id]: e.target.value }))
                        }
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                      >
                        {REFUND_OPTIONS.map((r) => (
                          <option key={r} value={r}>
                            {formatRefundOptionLabel(r, estimatedAmount)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      disabled={savingId === issue.id || !hasChanges}
                      onClick={() => handleSaveStatus(issue)}
                      className="w-full sm:w-auto rounded-lg bg-amber-700 px-4 py-2 font-semibold text-white disabled:opacity-50"
                    >
                      {savingId === issue.id ? 'Saving…' : 'Save & notify customer'}
                    </button>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
